<template>
  <network-button-window
    :window-name="$t('NetworkStartingPoint')"
    :parent-toggle-window="windowOpen"
    :list-missing-permissions="listMissingPermissions"
    :is-fixed="isFixed"
  >
    <template v-slot:buttonIcon>
      <NetworkIcon class="network-icon" />
    </template>

    <template v-slot:refreshButton>
      <v-btn
        x-small
        fab
        text
        color="accent"
        class="mr-0"
        style="height: 20px; width: 20px"
        :loading="refreshingOptions"
        @click.stop="getOptions"
      >
        <v-icon size="15">refresh</v-icon>
      </v-btn>
    </template>

    <template v-slot:windowContent>
      <treeselect
        v-if="isAdmin"
        v-model="workspace.uuid"
        :options="workspace.options"
        :normalizer="workspaceOptionsNormalizer"
        :disable-branch-nodes="true"
        :show-count="true"
        :clearable="false"
        class="starting-point-select"
        :class="{
          'network-starting-point-highlighted-select':
            isWorkspaceUndefined &&
            workspace.selectHighlighted &&
            listMissingPermissions.length === 0
        }"
        :placeholder="$tc('Workspace', 1)"
        :disabled="interactionsDisabled"
        disableFuzzyMatching
      />

      <treeselect
        v-model="startingPoint.uuid"
        :options="startingPoint.options"
        :disable-branch-nodes="true"
        :show-count="true"
        class="starting-point-select"
        :class="{
          'network-starting-point-highlighted-select':
            !isWorkspaceUndefined &&
            startingPoint.selectHighlighted &&
            listMissingPermissions.length === 0
        }"
        :placeholder="$tc('TechnicalLocal', 1)"
        :disabled="isWorkspaceUndefined || interactionsDisabled"
        disableFuzzyMatching
      />
      
      <treeselect
          v-model="closures.uuid"
          :options="closures.options"
          :disable-branch-nodes="false"
          :show-count="true"
          class="starting-point-select"
          :class="{
              'network-starting-point-highlighted-select':
                !isWorkspaceUndefined &&
                closures.selectHighlighted &&
                listMissingPermissions.length === 0
            }"
          :placeholder="$tc('Closures', 1)"
          :disabled="isWorkspaceUndefined || closureSelectionDisabled || !closureDisabled"
          disableFuzzyMatching
        />

      <home-window-button
        :label="$t('Submit')"
        @buttonClick="submit"
        :disabled="submitDisabled"
      />
    </template>
  </network-button-window>
</template>

<script>
import { workspaceTechSitesLocalsMixin } from "@/mixins/workspaceTechSitesLocalsMixin";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
import NetworkIcon from "@/assets/home/tabs/network/network.svg";
import NetworkButtonWindow from "../Network/NetworkButtonWindow";
import ProcessesButton from "../Network/Calculations/ProcessesButton";
import HomeWindowButton from "../../HomeWindowButton";
import MissingPermissionsTooltip from "@/components/_Atoms/MissingPermissionsTooltip";
import { mapActions } from "vuex";
export default {
  name: "NetworkDiagramStartingPoint",

  components: {
    MissingPermissionsTooltip,
    HomeWindowButton,
    ProcessesButton,
    NetworkButtonWindow,
    NetworkIcon,
  },

  mixins: [workspaceTechSitesLocalsMixin, checkPermissionsMixin],

  props: {
    /**
     * If the window is supposed to open and close or be always open.
     */
    isFixed: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      /** State of the Starting Point window: true = open; false = minimized. */
      windowOpen: true,

      workspace: {
        /** UUID of the selected Workspace. */
        uuid: undefined,
        /** List of options to show in the Workspace Select field. */
        options: [],
        /** If the Workspace Select field is supposed to be highlighted or not */
        selectHighlighted: false
      },

      drawNetwork: [],

      closureDisabled: false,

      startingPoint: {
        /** UUID of the selected Technical Local. */
        uuid: undefined,
        /** List of options to show in the Technical Local Select field. */
        options: [],
        /** If the Technical Local Select field is supposed to be highlighted or not */
        selectHighlighted: false
      },

      closures: {
        /** UUID of the selected Closure. */
        uuid: undefined,
        /** List of options to show in the Closures Select field. */
        options: [],
        /** If the closure field is supposed to be highlighted or not */
        selectHighlighted: false
      },

      refreshingOptions: false
    };
  },

  computed: {
    storeTechnicalLocalUUID() {
      return this.$store.getters.getNetworkDiagramTechnicalLocalUUID;
    },

    storeWorkspaceUuid() {
      return this.$store.getters.getNetworkDiagramWorkspaceUUID;
    },

    /**
     * Checks if the workspace is undefined, in case the user is Admin.
     * returns {boolean}
     */
    isWorkspaceUndefined() {
      return this.isAdmin && this.workspace.uuid === undefined;
    },

    interactionsDisabled() {
      return (
        this.disabled ||
        this.refreshingOptions ||
        this.listMissingPermissions.length > 0
      );
    },

    closureSelectionDisabled(){
      this.checkDiagramTechnicalId();
      if ((this.drawNetwork.tl_uuid && this.startingPoint.uuid !== undefined) || this.closures.uuid ) {
        if (this.drawNetwork.tl_uuid === undefined && this.startingPoint.uuid === "Technical Local") {
          this.closureDisabled=true;
        }
        return false;
      } 
    },

    submitDisabled() {
      return (
        this.interactionsDisabled ||
        (this.isFixed &&
          this.startingPoint.uuid === this.storeTechnicalLocalUUID)
      );
    },

    currentWorkspaceUuid() {
      return this.$store.getters.workspace.uuid;
    },

    listMissingPermissions() {
      let missingPermissions = [];
      if (!this.userHasFibercloudPermission("technical_local_list")) {
        missingPermissions.push("technical_local_list");
      }
      if (!this.userHasFibercloudPermission("technical_site_list")) {
        missingPermissions.push("technical_site_list");
      }
      return missingPermissions;
    }
  },

  watch: {
    /**
     * Makes the Workspace Select field change to no highlighted if a Workspace is selected and send a request for
     * the respective list of technical locals.
     * @param newUUID - UUID of the newly selected Workspace.
     */
    "workspace.uuid"(newUUID) {
      this.workspace.selectHighlighted = newUUID === undefined;
      this.startingPoint.selectHighlighted = newUUID !== undefined;
      this.RESET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID().then(() => {
        this.startingPoint.uuid = undefined;
      });

      this.SET_NETWORK_DIAGRAM_WORKSPACE_UUID(newUUID).then(() => {
        this.getTechnicalSitesWithTechnicalLocals(newUUID);
      });
    },

    /**
     * Makes the Technical Local Select field change to no highlighted if a Technical Local is selected.
     * @param newUUID - UUID of the newly selected Technical Local.
     */
    "startingPoint.uuid"(newUUID) {
      this.startingPoint.selectHighlighted = newUUID === undefined;
      this.closureDisabled = this.drawNetwork.tl_uuid === this.startingPoint.uuid;
      this.closures.options = [];
      if (!(this.startingPoint.uuid === "Technical Local" && newUUID === "Technical Local")) {
        this.closures.uuid = undefined;
      }
      if (this.startingPoint.uuid === undefined) {
        this.getClosuresFromSelectedTechnicalLocal();
        this.closureDisabled=true;
      }
      if(this.drawNetwork.tl_uuid === newUUID && this.closures.options.length === 0){
        let connectedClosures=[];
          this.extractCLData(this.drawNetwork,connectedClosures);
          this.closures.options = this.getClosuresOptions(
            connectedClosures
          );
      }
      this.SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID(newUUID)
    },

    storeTechnicalLocalUUID(newValue) {
      if (this.startingPoint.uuid !== newValue) {
        this.startingPoint.uuid = newValue;
      }
    },

    storeWorkspaceUuid(newValue) {
      if (newValue) {
        this.workspace.uuid = newValue;
      }
    },

    currentWorkspaceUuid(newValue) {
      this.workspace.uuid = newValue;
    },

    listMissingPermissions() {
      if (
        this.listMissingPermissions &&
        this.listMissingPermissions.length > 0
      ) {
        this.windowOpen = false;
      }
    }
  },

  created() {
    this.getOptions();
    if (this.listMissingPermissions && this.listMissingPermissions.length > 0) {
      this.windowOpen = false;
    }
  },

  methods: {
    ...mapActions([
    "SET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID",
    ]),

    getOptions() {
      this.refreshingOptions = true;
      if (this.isAdmin) {
        this.getWorkspaces();
      } else {
        this.getTechnicalSitesWithTechnicalLocals(
          this.$store.getters.workspace.uuid
        );
        this.getClosuresFromSelectedTechnicalLocal()
      }
    },

    getWorkspaces() {
      this.fetchAllWorkspaces({
        includes: null,
        fields: "wp_uuid,wp_label,wp_id"
      }).then(workspaces => {
        this.workspace.options = workspaces;

        if (this.storeWorkspaceUuid) {
          this.workspace.uuid = this.storeWorkspaceUuid;
        } else {
          this.workspace.uuid = this.$store.getters.workspace.uuid;
        }

        if (this.workspace.uuid === undefined) {
          if (!this.isFixed) {
            this.windowOpen = true;
          }
          this.workspace.selectHighlighted = true;
          this.refreshingOptions = false;
        } else {
          this.getTechnicalSitesWithTechnicalLocals(this.workspace.uuid);
        }
      });
    },
    /**
     * Sends a request to the store to get the list of Technical Sites with Technical Locals from the server for the
     * selected workspace
     * @param {string} workspaceUuid - UUID of the selected workspace
     */
    getTechnicalSitesWithTechnicalLocals(workspaceUuid) {
      if (this.listMissingPermissions.length === 0) {
        this.GET_WORKSPACE_TECHNICAL_SITES_WITH_LOCALS(workspaceUuid).then(
          technicalSitesWithLocals => {
            this.startingPoint.options = this.getTechnicalSitesWithLocalsOptions(
              technicalSitesWithLocals
            );
            this.startingPoint.uuid = this.storeTechnicalLocalUUID;
            if (this.startingPoint.uuid === undefined) {
              if (!this.isFixed) {
                this.windowOpen = true;
              }
              this.startingPoint.selectHighlighted = true;
            }
            this.refreshingOptions = false;
          }
        );
      } else {
        this.refreshingOptions = false;
      }
    },

    /**
     * Sends a request to the store to get the list of Closures connected with the Technical Local or all the workspace closures
     */
    getClosuresFromSelectedTechnicalLocal() {
      let workspaceUuid = this.$store.getters.workspace.uuid;
      if (this.listMissingPermissions.length === 0) {
        if (this.startingPoint.uuid === undefined) {
          this.GET_WORKSPACE_CLOSURES(workspaceUuid).then(    
          closures => {
            this.closures.options = this.getClosuresOptions(
              closures
            );
            this.refreshingOptions = false;
          }
        );
        } else {
          let connectedClosures=[];
          this.extractCLData(this.drawNetwork,connectedClosures);
          this.closures.options = this.getClosuresOptions(
            connectedClosures
          );
          this.refreshingOptions = false;
          this.closureDisabled = true;
        }
      } else {
        this.refreshingOptions = false;
      }
    },

    extractCLData(data, result) {
      let label;
      if (typeof data === 'object' && data !== null) {
        if ('cl_id' in data || 'cl_label' in data || 'cl_uuid' in data) {
          if (data.cl_label) {
            label = data.cl_label;
          }else{
            label = data.cl_id;
          }
          result.push({ cl_uuid: data.cl_uuid, cl_label: label });
        }
        for (let key in data) {
          if (JSON.stringify(data[key]).includes("-")) {
            this.extractCLData(data[key], result);
          }
        }
      }
    },

    /**
     * Sends the selected Technical Local UUID to the store and requests the network data from the backend (if the UUID
     * is not
     * undefined).
     */
    submit() {
      let uuid = this.startingPoint.uuid;
      if (uuid || this.closures.uuid) {
        this.SET_NETWORK_DIAGRAM_CLOSURE_UUID(this.closures.uuid);
        this.$emit("submit", uuid,this.closures.uuid,this.getClosuresFromSelectedTechnicalLocal);
      } else {
        this.RESET_NETWORK_DIAGRAM_TECHNICAL_LOCAL_UUID().then(() => {
          this.startingPoint.selectHighlighted = true;
        });
      }
    },

    /**
     * Normalizer for the list of workspace options (so that the treeselect component can read it).
     * @param {object} node - Original Workspace object
     * @return {object} the adapted object
     */
    workspaceOptionsNormalizer(node) {
      return {
        id: node.wp_uuid,
        label: node.wp_label
      };
    },

    checkDiagramTechnicalId(){
      let networkData = this.$store.getters.getNetworkDiagramData;
      if (networkData !== undefined) {
        if (networkData.tl_uuid) {
          this.drawNetwork= networkData;
        }
      }
      return ;
    }
  }
};
</script>

<style scoped>
.network-icon {
  width: 25px;
}

.network-icon path:nth-child(2) {
  fill: var(--secondary-darker);
}

.starting-point-select {
  width: 100%;
  margin: 10px 0;
  font-size: 12px;
}
</style>

<style>
.network-starting-point-highlighted-select .vue-treeselect__control {
  border: 1px solid var(--primary-highlight);
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Content/Tabs/StartingPoint/StartingPoint.vue